import React,{useEffect,useState} from 'react'
import {Chip,Modal} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { DataGrid,esES } from '@mui/x-data-grid';
import { getAllVideos } from '../../../services/videos';
import { getAllMedia } from '../../../services/media';
import { createTheme, ThemeProvider} from '@mui/material/styles';
import { IconButton } from '@mui/material';
import { Delete, Info } from '@mui/icons-material';
import VideoUploadStepper from '../Stepper/Video/StepperVideoUpload';
import RemoveForm from '../Stepper/Video/RemoveForm';
import UpdateVideoForm from '../Stepper/Video/UpdateVideoForm';


const theme = createTheme(
    {
      palette: {
        primary: { main: '#1976d2' },
      },
    },
    esES, // x-data-grid translations
  );

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: '90vw',
    maxWidth:'600px',
    color:'black',
    bgcolor: "#FFFFFF",
    boxShadow: "0 8px 32px 0 rgba( 31, 38, 135, 0.37 )",
    border: "1px solid rgba( 255, 255, 255, 0.18 )",
    p: 2,
    borderRadius: 5,
    backdropFilter:'blur(4px)'
  };

const detectMediaType = (filePath = '') => {
  const normalizedPath = filePath.toLowerCase();

  if (normalizedPath.includes('.pdf')) {
    return 'pdf';
  }

  if (/(\.mp3|\.wav|\.ogg|\.m4a|\.aac|\.flac)/.test(normalizedPath)) {
    return 'audio';
  }

  return 'video';
};

const detectFileType = (item = {}) => {
  const payloadType = String(item?.mediaType || '').trim().toUpperCase();

  if (payloadType === 'AUDIO' || payloadType === 'PDF' || payloadType === 'VIDEO') {
    return payloadType.toLowerCase();
  }

  return detectMediaType(item?.filePath || '');
};

const formatPillLabel = (type = 'video') => String(type || 'video').toUpperCase();

const normalizeVideoRow = (video) => ({
  ...video,
  id: video?.id || video?._id || video?.videoId,
  fileType: 'video',
  tags: Array.isArray(video?.tags) ? video.tags : [],
  allowedSubscriptions: Array.isArray(video?.allowedSubscriptions) ? video.allowedSubscriptions : [],
});

const normalizeMediaRow = (media) => ({
  ...media,
  id: media?.id || media?._id || media?.mediaId,
  videoId: media?.mediaId || media?._id,
  fileType: detectFileType(media),
  tags: Array.isArray(media?.tags) ? media.tags : [],
  allowedSubscriptions: Array.isArray(media?.allowedSubscriptions) ? media.allowedSubscriptions : [],
  title: media?.title || 'Media',
});

const VideosTable = ({ mediaType = 'all' }) => {

  const [videosList, setVideosList] = useState([]);
  const [currentVideo,setCurrentVideo] = useState();
  const [activeOption,setActiveOption] =useState();
  const [isLoading, setIsLoading] = useState(true);
  const [error,setError] = useState();
  const [open, setOpen] = useState(false);

  const tableTitle = mediaType === 'all'
    ? 'Media'
    : mediaType === 'audio'
      ? 'Audio'
      : mediaType === 'pdf'
        ? 'PDF'
        : 'Video';


    useEffect(()=>{
      getAllVideosList();
    },[])

    const columns = [
      { field: 'videoId', headerName: 'ID', width: 300 },
      { field: 'fileType', headerName: 'Tipo', width: 130, renderCell: (params) => {
        const type = String(params.value || 'video').toLowerCase();
        const bgColor = type === 'pdf' ? '#FECE79' : type === 'audio' ? '#E6A341' : '#B14A36';
        const textColor = type === 'audio' ? '#1f2937' : '#ffffff';

        return (
          <Chip
            label={formatPillLabel(type)}
            style={{ background: bgColor, color: textColor, fontWeight: 600 }}
            size="small"
          />
        );
      } },
      { field: 'title', headerName: 'Titulo', width: 300 }, 
      { field: 'tags', headerName: 'Tags', width: 320, renderCell: (params) => (
        <ul style={{ padding: 0, margin: 0, listStyle: 'none' }}>
        {(Array.isArray(params.value) ? params.value : []).slice(0, 2).map((plan, index) => (
          <Chip key={index} label={`${plan.substring(0, 7)}...`} style={{ marginRight: '4px',background:'#FECE79' }} />
          ))}
          {(Array.isArray(params.value) ? params.value.length : 0) > 2 && (
              <Chip key="more" label={`+${(Array.isArray(params.value) ? params.value.length : 0) - 2} mas`} style={{ marginRight: '4px',background:'#FECE79' }} />
          )}
      </ul>
        ), },
        { field: 'allowedSubscriptions', headerName: 'Subscripciones', width: 300, renderCell: (params) => (
          <ul style={{ padding: 0, margin: 0, listStyle: 'none' }}>
            {(Array.isArray(params.value) ? params.value : []).slice(0, 2).map((plan, index) => (
              <Chip key={index} label={`${plan.substring(0, 7)}...`} style={{ marginRight: '4px',background:'#E6A341' }} />
              ))}
              {(Array.isArray(params.value) ? params.value.length : 0) > 2 && (
                  <Chip key="more" label={`+${(Array.isArray(params.value) ? params.value.length : 0) - 2} mas`} style={{ marginRight: '4px',background:'#E6A341' }} />
              )}
          </ul>
        ), },
        { field: 'actions', headerName: 'Acciones', width: 300, renderCell: (params) => (
          <div className="flex gap-2">
            <IconButton aria-label="info" size="small" onClick={() =>updateVideo(params.row)}>
              <Info />
            </IconButton>
            <IconButton aria-label="remove" size="small" onClick={() =>removeVideo(params.row)}>
              <Delete />
            </IconButton>
          </div>
        ), }
    ];
  

    const handleOpen = async (option) => {
      try {
        setOpen(true);
        setActiveOption(option);
      } catch (error) {
        console.error('Error while opening:', error);
      }
    };

    const handleClose = () => {
      setOpen(false)
    };
  
    const getAllVideosList =  async () => {
        try{     
            setIsLoading(true);
            const [videosResponse, mediaResponse] = await Promise.all([
              getAllVideos(),
              getAllMedia(),
            ]);

            const videosData = Array.isArray(videosResponse?.data) ? videosResponse.data : [];
            const mediaData = Array.isArray(mediaResponse?.data) ? mediaResponse.data : [];

            const normalizedVideos = videosData.map((video) => normalizeVideoRow(video));
            const normalizedMedia = mediaData.map((media) => normalizeMediaRow(media));

            const data = [...normalizedVideos, ...normalizedMedia];
            const filteredData = mediaType === 'all'
              ? data
              : data.filter((item) => detectFileType(item) === mediaType);
            setVideosList(filteredData)
        }catch(error){
            console.log(error);
            setError(error)
        }finally{
            setIsLoading(false);
        }
      };

    const getVideoInfo=(video) =>{
      try {
       setCurrentVideo(video);
       
      } catch (error) {
        console.log(error) 
      }
    }

    const removeVideo=(video) =>{
      setCurrentVideo(video);
      handleOpen('remove');
    }

    const updateVideo=(video) =>{
      setCurrentVideo(video);
      handleOpen('update');
    }

    const refreshData=()=>{
      handleClose();
      getAllVideosList();
    }
    
    const handleVideoOption =(action) =>{
      switch (action) {
        case 'add':
          return <VideoUploadStepper mediaType={mediaType} />;
        case 'update':
          return <UpdateVideoForm video={currentVideo} handleClose={handleClose} refreshData={refreshData}/>;
        case 'remove':
          return <RemoveForm video={currentVideo} handleClose={handleClose} refreshData={refreshData}/>;
        default:
          return <VideoUploadStepper mediaType={mediaType} />;
      }
    }
    
  return (
    <div style={{ height: 'auto', width: '100%' }}>

        
    {isLoading ? (
      <div className="flex justify-center items-center">
          <CircularProgress />
        </div>
      ): error ? (
        <div>Error: {error.message}</div>
      ) : (
        <ThemeProvider theme={theme}>
        <DataGrid
          rows={videosList}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 5 },
            },
          }}
          pageSizeOptions={[5, 10]}
          getRowId={(row) => row.id || row.videoId || row.mediaId || row._id}
          autoHeight
        />
        </ThemeProvider>
      )}

    <div className="flex justify-center p-5">
      <button className='button-29 w-auto'  onClick={() => handleOpen('add')}>Agregar {tableTitle}</button>
    <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >        
       {handleVideoOption(activeOption)}
    </Modal>
    </div>

     </div>
  )
}

export default VideosTable